import { useQuery } from '@tanstack/react-query';
import { publicGet } from '@/lib/api/publicClient';
import { publicTrackingEndpoints } from '@/lib/api/endpoints';

export interface TrackingEta {
  arrivalTime: string;
  driveMinutes: number | null;
  source: 'live' | 'static';
}

export interface TrackingTechLocation {
  lat: number;
  lng: number;
  updatedAt: string | null;
}

export interface TrackingBusiness {
  name: string | null;
  logo: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
}

export interface TrackingPage {
  customerName: string;
  serviceType: string | null;
  technicianName: string | null;
  status: string;
  timezone: string;
  startAt: string | null;
  arrivalWindow: { start: string; end: string } | null;
  destination: { lat: number; lng: number } | null;
  /** The human-readable service address, shown on the arrival row. */
  destinationLabel: string | null;
  eta: TrackingEta | null;
  techLocation: TrackingTechLocation | null;
  /** The tenant's public branding (name/logo/contact), for the header + footer. */
  business: TrackingBusiness | null;
  expiresAt: string | null;
}

export interface TrackingData {
  status: string;
  eta: TrackingEta | null;
  techLocation: TrackingTechLocation | null;
  updatedAt: string | null;
}

/** §9.4 — initial tracking-page payload (tech + service + first ETA/position). */
export function usePublicTrackingPage(token: string) {
  return useQuery<TrackingPage>({
    queryKey: ['public', 'tracking', token, 'page'],
    queryFn: async () => {
      const res = await publicGet<{ data: TrackingPage }>(publicTrackingEndpoints.page(token));
      return res.data;
    },
    retry: false,
    enabled: Boolean(token),
  });
}

/** §9.4 — the live position + ETA, polled every ~30s while the page is open. */
export function usePublicTrackingData(token: string, enabled: boolean) {
  return useQuery<TrackingData>({
    queryKey: ['public', 'tracking', token, 'data'],
    queryFn: async () => {
      const res = await publicGet<{ data: TrackingData }>(publicTrackingEndpoints.data(token));
      return res.data;
    },
    retry: false,
    enabled: enabled && Boolean(token),
    refetchInterval: 30_000,
  });
}
